import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Activity, Database, Server, RefreshCw, CheckCircle2, XCircle, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function ServiceCard({ name, icon: Icon, status, latency }) {
  const isUp = status === 'up';
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center justify-between p-5 rounded-xl border border-[hsl(var(--border))] bg-white"
      data-testid={`service-card-${name.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-center gap-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isUp ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
          <Icon size={20} />
        </div>
        <div>
          <p className="font-semibold text-[hsl(var(--foreground))]" style={{ fontFamily: 'Manrope, sans-serif' }}>{name}</p>
          {latency !== null && <p className="text-xs text-[hsl(var(--muted-foreground))]">{latency}ms response</p>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {isUp ? (
          <>
            <CheckCircle2 size={18} className="text-emerald-500" />
            <span className="text-sm font-medium text-emerald-600" data-testid={`status-${name.toLowerCase().replace(/\s/g, '-')}`}>Operational</span>
          </>
        ) : (
          <>
            <XCircle size={18} className="text-red-500" />
            <span className="text-sm font-medium text-red-600" data-testid={`status-${name.toLowerCase().replace(/\s/g, '-')}`}>Down</span>
          </>
        )}
      </div>
    </motion.div>
  );
}

export default function StatusPage() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastCheck, setLastCheck] = useState(null);
  const [latency, setLatency] = useState(null);

  const fetchHealth = async () => {
    setLoading(true);
    const start = performance.now();
    try {
      const res = await fetch(`${API}/api/health`);
      const elapsed = Math.round(performance.now() - start);
      setLatency(elapsed);
      if (res.ok) {
        const data = await res.json();
        setHealth(data);
      } else {
        setHealth({ status: 'degraded', services: { api: 'down', mongodb: 'down' }, version: '?' });
      }
    } catch {
      setLatency(null);
      setHealth({ status: 'degraded', services: { api: 'down', mongodb: 'down' }, version: '?' });
    }
    setLastCheck(new Date());
    setLoading(false);
  };

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const allUp = health?.status === 'healthy';

  return (
    <div className="min-h-screen bg-[hsl(var(--muted))]" data-testid="status-page">
      {/* Header */}
      <header className="bg-white border-b border-[hsl(var(--border))]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors" aria-label="Back to home" data-testid="status-back-link">
              <ArrowLeft size={20} />
            </Link>
            <h1 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="status-title">
              Evenly Respond
              <span className="text-[hsl(var(--muted-foreground))] font-normal ml-2 text-base">System Status</span>
            </h1>
          </div>
          <button
            onClick={fetchHealth}
            disabled={loading}
            className="flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors disabled:opacity-50"
            aria-label="Refresh status"
            data-testid="status-refresh-btn"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10" id="main-content">
        {/* Overall status banner */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-xl p-6 mb-8 text-center ${allUp ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}
          data-testid="status-banner"
        >
          <div className="flex items-center justify-center gap-3 mb-1">
            {allUp ? (
              <CheckCircle2 size={28} className="text-emerald-500" />
            ) : (
              <XCircle size={28} className="text-red-500" />
            )}
            <h2
              className="text-xl font-bold"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="status-overall"
            >
              {allUp ? 'All Systems Operational' : 'Service Disruption Detected'}
            </h2>
          </div>
          {lastCheck && (
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2" data-testid="status-last-check">
              Last checked: {lastCheck.toLocaleTimeString()}
              {latency !== null && ` (${latency}ms)`}
            </p>
          )}
        </motion.div>

        {/* Service cards */}
        <div className="space-y-3" data-testid="service-list">
          <ServiceCard
            name="API Server"
            icon={Server}
            status={health?.services?.api || 'down'}
            latency={latency}
          />
          <ServiceCard
            name="Database"
            icon={Database}
            status={health?.services?.mongodb || 'down'}
            latency={null}
          />
        </div>

        {/* Meta info */}
        {health && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-10 text-center text-xs text-[hsl(var(--muted-foreground))] space-y-1"
            data-testid="status-meta"
          >
            <p>Version: <span className="font-mono">{health.version}</span></p>
            <p>Environment: <span className="font-mono">{health.environment}</span></p>
            <p className="pt-2">Auto-refreshes every 30 seconds</p>
          </motion.div>
        )}
      </main>
    </div>
  );
}
